// ─────────────────────────────────────────────────────────────────────────────
// StartupSweepController.js
// Timing engine for the Startup Readiness Sweep (spec UI-LAUNCH-001 §4.1–4.2).
//
// Phase timeline (ADMIN_FULL, max 1200ms):
//   IDLE        — not yet started
//   SHELL       — 0–80ms     overlay mounts, real dashboard visible behind
//   BRAND       — 80–180ms   SFM brand ignition
//   TRACER      — 180–600ms  readiness sweep tracer fires across 5 stations
//   KPI_REVEAL  — 400–850ms  KPI tiles animate in (overlaps tracer)
//   MILESTONE   — 550–950ms  station milestones settle
//   RISK        — 850–1050ms priority risk card emphasis
//   COMPLETE    — 1000–1200ms overlay fades out
//   SETTLED     — post-fade  overlay unmounted
//
// Fast-forward: any user interaction during sweep → jump to SETTLED in <100ms
// Reduced-motion: single 150–250ms fade-and-settle, no tracer
// ─────────────────────────────────────────────────────────────────────────────
import { useState, useEffect, useRef, useCallback } from 'react';

// ── Constants (§5) ─────────────────────────────────────────────────────────
const SWEEP_FULL_MAX_MS      = 1200;
const SWEEP_COMPACT_MAX_MS   = 900;
const SWEEP_SKIP_ON_INPUT_MS = 100;
const REDUCED_MOTION_MS      = 200;

// ── Phase enum ──────────────────────────────────────────────────────────────
export const SweepPhase = Object.freeze({
  IDLE:       'IDLE',
  SHELL:      'SHELL',
  BRAND:      'BRAND',
  TRACER:     'TRACER',
  KPI_REVEAL: 'KPI_REVEAL',
  MILESTONE:  'MILESTONE',
  RISK:       'RISK',
  COMPLETE:   'COMPLETE',
  SETTLED:    'SETTLED',
});

// ── Timing map (ms from sweep start) ───────────────────────────────────────
const FULL_TIMELINE = [
  { phase: SweepPhase.SHELL,      at: 0   },
  { phase: SweepPhase.BRAND,      at: 80  },
  { phase: SweepPhase.TRACER,     at: 180 },
  { phase: SweepPhase.KPI_REVEAL, at: 400 },
  { phase: SweepPhase.MILESTONE,  at: 550 },
  { phase: SweepPhase.RISK,       at: 850 },
  { phase: SweepPhase.COMPLETE,   at: 1000},
  { phase: SweepPhase.SETTLED,    at: SWEEP_FULL_MAX_MS + 50 },
];

const COMPACT_TIMELINE = FULL_TIMELINE.map((e) => ({
  ...e,
  at: Math.round(e.at * (SWEEP_COMPACT_MAX_MS / SWEEP_FULL_MAX_MS)),
}));

// ── Cold-launch detection ──────────────────────────────────────────────────
// Sets a sessionStorage key on first load so the sweep only fires once per
// browser session (not on every SPA route change).
const SESSION_KEY = 'sfm_sweep_fired';

function detectColdLaunch() {
  try {
    if (sessionStorage.getItem(SESSION_KEY)) return false;
    sessionStorage.setItem(SESSION_KEY, '1');
    return true;
  } catch {
    return false; // storage blocked → treat as warm, don't show sweep
  }
}

// ── Feature flag ───────────────────────────────────────────────────────────
function isFeatureEnabled() {
  try {
    if (localStorage.getItem('SFM_STARTUP_SWEEP_ENABLED') === 'false') return false;
    // Enabled by default in production; disable via localStorage for testing
    return true;
  } catch {
    return false;
  }
}

// ── Reduced-motion detection ───────────────────────────────────────────────
function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/**
 * useStartupSweepController({ mode, onSettled })
 *
 * @param {string}   mode       From StartupSweepGate — one of:
 *                              'ADMIN_FULL' | 'ADMIN_COMPACT' | 'KIOSK_MICRO'
 *                              | 'REDUCED_MOTION' | 'DISABLED'
 * @param {Function} onSettled  Called when overlay should be removed from DOM
 *
 * @returns {{
 *   phase: string,
 *   tracerPct: number,   // 0–100, drives the tracer line width
 *   fastForward: () => void
 * }}
 */
export function useStartupSweepController({ mode, onSettled }) {
  const [phase,     setPhase]     = useState(SweepPhase.IDLE);
  const [tracerPct, setTracerPct] = useState(0);
  const timersRef = useRef([]);
  const rafRef    = useRef(null);
  const startRef  = useRef(null);
  const settledRef = useRef(false);

  const clearAll = useCallback(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const settle = useCallback(() => {
    if (settledRef.current) return;
    settledRef.current = true;
    clearAll();
    setPhase(SweepPhase.SETTLED);
    setTracerPct(100);
    if (onSettled) onSettled();
  }, [clearAll, onSettled]);

  const fastForward = useCallback(() => {
    if (phase === SweepPhase.IDLE || phase === SweepPhase.SETTLED) return;
    clearAll();
    // Jump to COMPLETE then settle after SWEEP_SKIP_ON_INPUT_MS
    setPhase(SweepPhase.COMPLETE);
    setTracerPct(100);
    const t = setTimeout(settle, SWEEP_SKIP_ON_INPUT_MS);
    timersRef.current.push(t);
  }, [phase, clearAll, settle]);

  // ── Tracer animation via rAF ──────────────────────────────────────────
  const animateTracer = useCallback((tracerStartMs, tracerEndMs) => {
    const duration = tracerEndMs - tracerStartMs;
    const tick = (now) => {
      if (!startRef.current) return;
      const elapsed = now - startRef.current - tracerStartMs;
      const pct = Math.min(100, Math.max(0, (elapsed / duration) * 100));
      setTracerPct(pct);
      if (pct < 100) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (mode === 'DISABLED') return;

    const reducedMotion = mode === 'REDUCED_MOTION' || prefersReducedMotion();
    const timeline = mode === 'ADMIN_COMPACT' ? COMPACT_TIMELINE : FULL_TIMELINE;

    startRef.current = performance.now();
    settledRef.current = false;

    if (reducedMotion) {
      // Spec §6.2: single fade-and-settle, no tracer
      setPhase(SweepPhase.BRAND);
      setTracerPct(100);
      const t = setTimeout(settle, REDUCED_MOTION_MS);
      timersRef.current.push(t);
      return () => clearAll();
    }

    // Schedule each phase transition
    for (const entry of timeline) {
      const t = setTimeout(() => {
        setPhase(entry.phase);
        if (entry.phase === SweepPhase.SETTLED) settle();
      }, entry.at);
      timersRef.current.push(t);
    }

    // Kick off rAF-driven tracer between TRACER start and KPI_REVEAL start
    const tracerEntry   = timeline.find((e) => e.phase === SweepPhase.TRACER);
    // eslint-disable-next-line no-unused-vars
    const kpiEntry      = timeline.find((e) => e.phase === SweepPhase.KPI_REVEAL);
    const milestoneEntry= timeline.find((e) => e.phase === SweepPhase.MILESTONE);
    if (tracerEntry && milestoneEntry) {
      const tTracerStart = setTimeout(
        () => animateTracer(tracerEntry.at, milestoneEntry.at),
        tracerEntry.at
      );
      timersRef.current.push(tTracerStart);
    }

    return () => clearAll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  return { phase, tracerPct, fastForward };
}

export { detectColdLaunch, isFeatureEnabled, prefersReducedMotion };
